import { Grid, Typography } from "@mui/material";
import {
  AggregateScoreType,
  OrderDirection,
  SearchItemStatus,
} from "~/operations";
import {
  NoSearchResults,
  SearchFilterSection,
  SearchResultsTable,
  useFullTextSearch,
} from ".";
import { Org } from "~/lib/types";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { pluralize } from "~/lib/pluralize";
import { NetworkStatus } from "@apollo/client";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useScope } from "~/hooks/useScope";
import { CveSearchResultsEmptyState } from "./CveSearchResultsEmptyState";
import { isVulnSearchQuery } from "~/pages/Search/utils";

type FullTextSpacePage = {
  scope: SpaceOrWorkspaceScope;
  org?: never;
};

type FullTextOrgPage = {
  scope?: never;
  org: Org;
};

export type FullTextSearchResultsProps = FullTextSpacePage | FullTextOrgPage;

export function FullTextSearchResults({
  org,
  scope,
}: FullTextSearchResultsProps) {
  const { spaceScope } = useScope();
  const { activeFilter, counts, handle, page, query } = useFullTextSearch({
    scope: scope ? scope : org,
  });

  if (query.loading) {
    return <LoadingPage what="search results" />;
  }

  if (query.error) {
    return <LoadingFailedPage what="search results" />;
  }

  const isNetworkReady = query.networkStatus === NetworkStatus.ready;
  const { edges, pageInfo, scoreTypeDistribution } = query.data?.search ?? {};

  if ((!edges || !edges.length) && isNetworkReady) {
    if (isVulnSearchQuery(query.searchQuery)) {
      const commonProps = {
        searchQuery: query.searchQuery,
        name: scope ? scope.name : org.name,
        space: spaceScope
          ? { id: spaceScope.id, mrn: spaceScope.mrn, name: spaceScope.name }
          : undefined,
        scope,
      } as const;

      return <CveSearchResultsEmptyState {...commonProps} />;
    }

    return (
      <NoSearchResults
        searchQuery={query.searchQuery}
        name={scope ? scope.name : org.name}
        showSearchInput={true}
      />
    );
  }

  // When status exists, it means that the search result is a CVE
  const firstCveSearchStatus = edges?.[0]?.searchItemStatus ?? undefined;
  const hasNonAffectedCveStatus =
    firstCveSearchStatus && firstCveSearchStatus !== SearchItemStatus.Affected;
  if (
    isVulnSearchQuery(query.searchQuery) &&
    hasNonAffectedCveStatus &&
    isNetworkReady
  ) {
    const commonProps = {
      searchQuery: query.searchQuery,
      name: scope ? scope.name : org.name,
      space: spaceScope
        ? { id: spaceScope.id, mrn: spaceScope.mrn, name: spaceScope.name }
        : undefined,
      scope,
      status: firstCveSearchStatus,
    } as const;

    return <CveSearchResultsEmptyState {...commonProps} />;
  }

  return (
    <Grid container spacing={5}>
      <Grid sx={{ display: "flex", justifyContent: "space-between" }} size={12}>
        <Typography variant="h5" component="h2" fontWeight={700}>
          {counts.totalCount} {pluralize("result", counts.totalCount)} for "
          {query.searchQuery}" in {org?.name || scope?.name}
        </Typography>
      </Grid>
      <Grid
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
        size={12}
      >
        <SearchFilterSection
          {...{
            activeFilter,
            toggleFilter: handle.toggleFilter,
            scoreTypeDistribution,
          }}
        />
      </Grid>
      <Grid size={12}>
        <SearchResultsTable
          {...{
            data: edges?.slice(page.items.from, page.items.to),
            loading: query.loading,
            pageType: activeFilter as AggregateScoreType,
            field: "name",
            direction: OrderDirection.Asc,
            isOrg: Boolean(org),
            handleSortClick: () => {}, // sorting not enabled yet
            paginationProps: {
              fetchMore: query.fetchMore,
              pageInfo,
              setPageItems: page.setItems,
              totalCount: counts.filteredTotalCount,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}
